/**
 * Coaching data access: TanStack Query hooks over the typed client. Every write goes through
 * the module's actions (REST endpoints that call `perform`) and returns an ActionOut.
 * Other modules (me) use these hooks; never the pages.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type {
  ActionOut,
  CancelSessionBody,
  CoachProfileInBody,
  DriverFocusBody,
  ImpactMetric,
  LogTime,
  PlanSession,
  RecordOutcomeBody,
  RescheduleSessionBody,
  SessionPatch,
} from './types';

export const coachingKeys = {
  all: ['coaching'] as const,
  workspace: ['coaching', 'workspace'] as const,
  priority: (p: object) => ['coaching', 'priority', p] as const,
  breakdown: (driverId: string) => ['coaching', 'breakdown', driverId] as const,
  focus: (driverId: string) => ['coaching', 'focus', driverId] as const,
  evidence: (driverId: string, p: object) => ['coaching', 'evidence', driverId, p] as const,
  training: (driverId: string) => ['coaching', 'training', driverId] as const,
  cases: (p: object) => ['coaching', 'cases', p] as const,
  case: (id: string) => ['coaching', 'case', id] as const,
  prep: (caseId: string) => ['coaching', 'prep', caseId] as const,
  sessions: (p: object) => ['coaching', 'sessions', p] as const,
  session: (id: string) => ['coaching', 'session', id] as const,
  sessionTime: (id: string) => ['coaching', 'session-time', id] as const,
  timer: ['coaching', 'timer'] as const,
  timeSummary: (p: object) => ['coaching', 'time-summary', p] as const,
  coaches: (p: object) => ['coaching', 'coaches', p] as const,
  myCoach: ['coaching', 'coach-me'] as const,
  availability: (userId: string) => ['coaching', 'availability', userId] as const,
  slots: (userId: string, p: object) => ['coaching', 'slots', userId, p] as const,
  impact: (p: object) => ['coaching', 'impact', p] as const,
  impactCoaches: (p: object) => ['coaching', 'impact-coaches', p] as const,
  me: ['me', 'coaching'] as const,
  myScores: (days: number) => ['me', 'scores', days] as const,
};

/** Invalidate everything coaching (and the driver's own views) after a write. */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: coachingKeys.all });
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['ai', 'proposals'] });
    void qc.invalidateQueries({ queryKey: ['comms'] });
  };
}

function useAction<V>(fn: (v: V) => Promise<ActionOut>) {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
}

// ---------------------------------------------------------------- workspace and priority

export function useWorkspace(enabled = true) {
  return useQuery({ queryKey: coachingKeys.workspace, queryFn: () => unwrap(api.GET('/api/coaching/workspace')), enabled, refetchInterval: 60_000 });
}

export interface PriorityQuery {
  project?: string;
  band?: 'urgent' | 'high' | 'medium' | 'low';
  no_session?: boolean;
}

export function usePriority(p: PriorityQuery, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.priority(p),
    queryFn: () => unwrap(api.GET('/api/coaching/priority', { params: { query: { ...p, limit: 500 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePriorityBreakdown(driverId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.breakdown(driverId ?? ''),
    queryFn: () => unwrap(api.GET('/api/coaching/priority/{driver_id}', { params: { path: { driver_id: driverId ?? '' } } })),
    enabled: !!driverId && enabled,
    retry: (n, e) => n < 2 && !(e instanceof Error && 'status' in e && (e as { status: number }).status === 404),
  });
}

export function useRecalculate() {
  return useAction((project_id?: string) => unwrap(api.POST('/api/coaching/priority/recalculate', { body: { project_id: project_id ?? null } })));
}

export function useDriverFocus(driverId: string, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.focus(driverId),
    queryFn: () => unwrap(api.GET('/api/coaching/drivers/{driver_id}/focus', { params: { path: { driver_id: driverId } } })),
    enabled,
  });
}

export function useSaveFocus(driverId: string) {
  return useAction((body: DriverFocusBody) => unwrap(api.PUT('/api/coaching/drivers/{driver_id}/focus', { params: { path: { driver_id: driverId } }, body })));
}

export function useEvidence(driverId: string | undefined, p: { start?: string; end?: string } = {}) {
  return useQuery({
    queryKey: coachingKeys.evidence(driverId ?? '', p),
    queryFn: () => unwrap(api.GET('/api/coaching/drivers/{driver_id}/evidence', { params: { path: { driver_id: driverId ?? '' }, query: p } })),
    enabled: !!driverId,
  });
}

export function useTrainingReport(driverId: string, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.training(driverId),
    queryFn: () => unwrap(api.GET('/api/coaching/drivers/{driver_id}/training-report', { params: { path: { driver_id: driverId } } })),
    enabled,
  });
}

// ---------------------------------------------------------------- cases

export interface CaseQuery {
  stage?: string[];
  open?: boolean;
  driver?: string;
  coach?: string;
  need?: string;
}

export function useCases(p: CaseQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: coachingKeys.cases(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/coaching/cases', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCase(id: string) {
  return useQuery({ queryKey: coachingKeys.case(id), queryFn: () => unwrap(api.GET('/api/coaching/cases/{case_id}', { params: { path: { case_id: id } } })) });
}

export function useCasePrepHistory(caseId: string, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.prep(caseId),
    queryFn: () => unwrap(api.GET('/api/coaching/cases/{case_id}/prep', { params: { path: { case_id: caseId }, query: { limit: 10 } } })),
    enabled,
  });
}

/** A prep report for a case without a session (with a session, use the session's `prep` verb). */
export function useCasePrep(caseId: string) {
  return useAction((language?: 'en' | 'nl' | 'fr') =>
    unwrap(api.POST('/api/coaching/cases/{case_id}/prep', { params: { path: { case_id: caseId } }, body: { language: language ?? null } })),
  );
}

export interface OpenCaseInput {
  driver_id: string;
  need?: string;
  reason?: string;
  topics?: string[];
  detection_ids?: string[];
  trip_ids?: string[];
  coach_user_id?: string | null;
}

export function useOpenCase() {
  return useAction((body: OpenCaseInput) =>
    unwrap(api.POST('/api/coaching/cases', { body: { ...body, need: body.need ?? 'general', reason: body.reason ?? '' } })),
  );
}

export function useCaseVerb(caseId: string, verb: 'close' | 'cancel') {
  return useAction((outcome: string) =>
    verb === 'close'
      ? unwrap(api.POST('/api/coaching/cases/{case_id}/close', { params: { path: { case_id: caseId } }, body: { outcome } }))
      : unwrap(api.POST('/api/coaching/cases/{case_id}/cancel', { params: { path: { case_id: caseId } }, body: { outcome } })),
  );
}

// ---------------------------------------------------------------- sessions

export interface SessionQuery {
  driver?: string;
  coach?: string;
  case?: string;
  status?: string[];
  session_type?: string[];
  start?: string;
  end?: string;
  code95?: boolean;
}

export function useSessions(p: SessionQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: coachingKeys.sessions(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/coaching/sessions', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePlanSession() {
  return useAction((body: PlanSession) => unwrap(api.POST('/api/coaching/sessions', { body })));
}

export function useUpdateSession(id: string) {
  return useAction((body: SessionPatch) => unwrap(api.PATCH('/api/coaching/sessions/{session_id}', { params: { path: { session_id: id } }, body })));
}

export type SessionVerb =
  | { verb: 'invite'; body: { channel?: 'inapp' | 'email' | 'sms' | 'whatsapp' | 'push' | null; text?: string | null } }
  | { verb: 'confirm'; body: { scheduled_at?: string | null; notify: boolean } }
  | { verb: 'reschedule'; body: RescheduleSessionBody }
  | { verb: 'cancel'; body: CancelSessionBody }
  | { verb: 'no-show'; body: { note: string } }
  | { verb: 'outcome'; body: RecordOutcomeBody }
  | { verb: 'prep'; body: { case_id?: string | null; language?: 'en' | 'nl' | 'fr' | null } };

/** Run a session action (the state machine's verbs; an illegal move is a 409 with the reason). */
export function useSessionAction(sessionId: string) {
  return useAction((a: SessionVerb) => {
    const path = { session_id: sessionId };
    switch (a.verb) {
      case 'invite':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/invite', { params: { path }, body: a.body }));
      case 'confirm':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/confirm', { params: { path }, body: a.body }));
      case 'reschedule':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/reschedule', { params: { path }, body: a.body }));
      case 'cancel':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/cancel', { params: { path }, body: a.body }));
      case 'no-show':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/no-show', { params: { path }, body: a.body }));
      case 'outcome':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/outcome', { params: { path }, body: a.body }));
      case 'prep':
        return unwrap(api.POST('/api/coaching/sessions/{session_id}/prep', { params: { path }, body: a.body }));
    }
  });
}

export function useSessionTime(sessionId: string, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.sessionTime(sessionId),
    queryFn: () => unwrap(api.GET('/api/coaching/sessions/{session_id}/time', { params: { path: { session_id: sessionId } } })),
    enabled,
  });
}

// ---------------------------------------------------------------- time tracking

export function useTimer(enabled = true) {
  return useQuery({ queryKey: coachingKeys.timer, queryFn: () => unwrap(api.GET('/api/coaching/timer')), enabled, refetchInterval: 120_000 });
}

export function useStartTimer() {
  return useAction((body: { session_id?: string | null; case_id?: string | null; phase: 'preparation' | 'scheduling' | 'session' | 'follow_up' }) =>
    unwrap(api.POST('/api/coaching/timer/start', { body })),
  );
}

export function useStopTimer() {
  return useAction(() => unwrap(api.POST('/api/coaching/timer/stop')));
}

export function useLogTime() {
  return useAction((body: LogTime) => unwrap(api.POST('/api/coaching/time', { body })));
}

export function useTimeSummary(p: { month?: string; coach?: string }, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.timeSummary(p),
    queryFn: () => unwrap(api.GET('/api/coaching/time/summary', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------- coaches and availability

export function useCoaches(active: boolean | undefined = true, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.coaches({ active }),
    queryFn: () => unwrap(api.GET('/api/coaching/coaches', { params: { query: { active } } })),
    staleTime: 60_000,
    enabled,
  });
}

export function useMyCoach(enabled = true) {
  return useQuery({
    queryKey: coachingKeys.myCoach,
    queryFn: () => unwrap(api.GET('/api/coaching/coaches/me')),
    enabled,
    retry: false,
  });
}

export function useSaveCoach(userId: string) {
  return useAction((body: CoachProfileInBody) => unwrap(api.PUT('/api/coaching/coaches/{user_id}', { params: { path: { user_id: userId } }, body })));
}

export function useAvailability(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.availability(userId ?? ''),
    queryFn: () => unwrap(api.GET('/api/coaching/coaches/{user_id}/availability', { params: { path: { user_id: userId ?? '' } } })),
    enabled: !!userId && enabled,
  });
}

export interface WeeklyRule {
  weekday: number;
  start: string;
  end: string;
  valid_from?: string | null;
  valid_to?: string | null;
}

export function useSetAvailability(userId: string) {
  return useAction((weekly: WeeklyRule[]) =>
    unwrap(api.PUT('/api/coaching/coaches/{user_id}/availability', { params: { path: { user_id: userId } }, body: { weekly } })),
  );
}

export function useAddException(userId: string) {
  return useAction((body: { kind: 'block' | 'extra'; starts_at: string; ends_at: string; reason: string }) =>
    unwrap(api.POST('/api/coaching/coaches/{user_id}/exceptions', { params: { path: { user_id: userId } }, body })),
  );
}

export function useRemoveException() {
  return useAction((id: string) => unwrap(api.DELETE('/api/coaching/availability/{availability_id}', { params: { path: { availability_id: id } } })));
}

export function useSlots(userId: string | undefined, p: { days?: number; length_min?: number } = {}, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.slots(userId ?? '', p),
    queryFn: () => unwrap(api.GET('/api/coaching/coaches/{user_id}/slots', { params: { path: { user_id: userId ?? '' }, query: p } })),
    enabled: !!userId && enabled,
  });
}

// ---------------------------------------------------------------- planner and impact

export function useRunPlanner() {
  return useAction((body: { project_id?: string | null; coach_user_id?: string | null; dry_run: boolean }) =>
    unwrap(api.POST('/api/coaching/planner/run', { body })),
  );
}

export interface ImpactQuery {
  start?: string;
  end?: string;
  metric?: ImpactMetric;
  project?: string;
}

export function useImpact(p: ImpactQuery, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.impact(p),
    queryFn: () => unwrap(api.GET('/api/coaching/impact', { params: { query: p } })),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useImpactCoaches(p: Omit<ImpactQuery, 'project'>, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.impactCoaches(p),
    queryFn: () => unwrap(api.GET('/api/coaching/impact/coaches', { params: { query: p } })),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    enabled,
  });
}

// ---------------------------------------------------------------- driver self-service (/api/me)

export function useMyCoaching(enabled = true) {
  return useQuery({ queryKey: coachingKeys.me, queryFn: () => unwrap(api.GET('/api/me/coaching')), enabled });
}

export function useRespond(sessionId: string) {
  return useAction((body: { response: 'confirm' | 'decline' | 'propose'; slot?: number | null; proposed_times?: string[]; message: string }) =>
    unwrap(api.POST('/api/me/coaching/sessions/{session_id}/respond', { params: { path: { session_id: sessionId } }, body })),
  );
}

export function useMyScores(days = 30, enabled = true) {
  return useQuery({ queryKey: coachingKeys.myScores(days), queryFn: () => unwrap(api.GET('/api/me/scores', { params: { query: { days } } })), enabled });
}
